import { createContext } from 'react';
import type { ApiError } from '@app/api/ApiError';
import type { Me } from '@app/api/hooks/shell';
import type { components } from '@app/api/types';

/**
 * Contexto da sessão. Fora de `SessionProvider.tsx` porque aquele arquivo só
 * exporta componentes (`react-refresh/only-export-components`).
 */

export type Role = components['schemas']['PapelUsuario'];
export type User = components['schemas']['User'];

/**
 * Estados possíveis da sessão:
 *
 * - `carregando` — `getMe` ainda não respondeu;
 * - `autenticado` — há usuário; `papel` manda no que a interface mostra (FR-010);
 * - `anonimo` — 401 `nao_autenticado`/`sessao_expirada`: o guard leva a T1 (FR-007);
 * - `indisponivel` — qualquer outra falha (rota ainda não implementada, backend fora,
 *   rede). Não é motivo para expulsar ninguém do console: o servidor é quem decide
 *   cada ação (FR-009), então o shell segue em modo degradado com o menu completo.
 */
export type StateSession = 'carregando' | 'autenticado' | 'anonimo' | 'indisponivel';

export interface SessionValue {
  estado: StateSession;
  /**
   * `true` quando a sessão **existia e morreu** (401 `sessao_expirada`, ou um 401
   * em qualquer chamada durante o uso). Distingue "sua sessão expirou" de "entre
   * para continuar" na T1 (FR-007).
   */
  expirada: boolean;
  usuario: User | null;
  /** `null` quando não sabemos quem é (carregando ou indisponível). */
  papel: Role | null;
  tenant: Me['tenant'] | null;
  /** Exibição de **Entrar com Google** em T1 (FR-006). */
  googleLoginConfigurado: boolean;
  /** Erro de `getMe`, quando houver — usado no aviso de modo degradado. */
  erro: ApiError | null;
  /** Refaz `getMe` (depois de um login, por exemplo). */
  recarregar: () => void;
  /**
   * Marca a sessão como encerrada **agora** (FR-008). Sem isto o `Sair` dependeria
   * do cache do react-query envelhecer: o `getMe` anterior continua no observer
   * por um instante e a T1 devolveria o usuário ao console.
   */
  encerrar: () => void;
}

export const SessionContext = createContext<SessionValue | null>(null);
